import { Component, DestroyRef, OnInit, ViewContainerRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { BaseModalService } from './core/services/base-modal.service';

/** True when any route on the active branch asks to render without the header and footer. */
export function isChromeless(snapshot: ActivatedRouteSnapshot | null): boolean {
  for (let route = snapshot; route; route = route.firstChild) {
    if (route.data?.['chromeless']) return true;
  }
  return false;
}

const chunkReloadKey = 'bio-builder:chunk-reload';

export function isChunkLoadError(error: unknown): boolean {
  const value = error as { message?: string; name?: string };
  const message = typeof error === 'string' ? error : `${value?.name ?? ''} ${value?.message ?? ''}`;
  return /ChunkLoadError|Loading chunk .* failed|Failed to fetch dynamically imported module|Importing a module script failed|Failed to load module script/i.test(message);
}

export function recoverFromChunkLoadError(
  error: unknown,
  url: string,
  storage: Storage = sessionStorage,
  navigate: (destination: string) => void = destination => window.location.assign(destination)
): boolean {
  if (!isChunkLoadError(error)) return false;

  try {
    if (storage.getItem(chunkReloadKey)) return false;
    storage.setItem(chunkReloadKey, 'true');
    navigate(url);
    return true;
  } catch {
    return false;
  }
}

export function clearChunkLoadRecovery(storage: Storage = sessionStorage): void {
  try {
    storage.removeItem(chunkReloadKey);
  } catch {
    return;
  }
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private vc: ViewContainerRef,
    private baseModalService: BaseModalService,
    private router: Router
  ) {}
  title = 'bio-builder';
  chromeless = false;

  ngOnInit() {
    this.baseModalService.setViewContainer(this.vc);
    this.router.events?.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => isChromeless(this.router.routerState.snapshot.root)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(chromeless => {
      this.chromeless = chromeless;
      clearChunkLoadRecovery();
    });
    this.router.events?.pipe(
      filter((event): event is NavigationError => event instanceof NavigationError),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(event => recoverFromChunkLoadError(event.error, event.url));
  }
}
